<template>
    <div class="panel">
        <div class="half-row">
            <div class="col">
                <img
                    class="image"
                    :src="googleMapSrc"
                >
            </div>
            <div class="col">
                <div class="half-text-row">
                    <span class="subtitle">Operation Name</span>
                    {{ siteDetails.operationName }}
                </div>
                <div class="half-text-row">
                    <span class="subtitle">ESRD File Number</span>
                    {{ siteDetails.id }}
                </div>
                <div class="pill-row">
                    <span class="subtitle">Current Site Status</span>
                    <Pill
                        :text="siteDetails.currentSiteStatusDescription"
                        :color="siteStatusPillColor"
                    />
                </div>
                <div class="pill-row">
                    <span class="subtitle">Are All Substances Remediated to Criteria?</span>
                    <Pill
                        :text="allSubstancesRemediatedToCriteria"
                        :color="allSubstancesRemediatedToCriteriaColor"
                    />
                </div>
                <div class="pill-row shadded">
                    <span class="subtitle">Human Health Concerns or Exceedances</span>
                    <Pill
                        :text="siteDetails.humanHealthConcernsOrExceedances"
                        :color="humanHealthConcernsOrExceedancesColor"
                    />
                    <p class="notes">
                        <span class="toupper">Quick Rationale</span><br>{{ siteDetails.rationaleForSiteStatus }}
                    </p>
                </div>
                <div class="land-use">
                    <div class="criteria">
                        <div class="subtitle">
                            Land Use Criteria
                        </div>
                        <div
                            v-for="[image, text] in landUseCriteria"
                            :key="text"
                            class="land-type"
                        >
                            <img :src="image">
                            <p class="notes">
                                {{ text }}
                            </p>
                        </div>
                    </div>
                    <div class="most-sensitive">
                        <div class="subtitle">
                            Most Sensitive Land-Use<br>within 30m
                        </div>
                        <div class="land-type">
                            <img :src="mostSensitiveLandUseWithin30mImage">
                            <p class="notes">
                                {{ mostSensitiveLandUseWithin30mLabel }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="fifth-text-row">
            <span class="subtitle">Activity</span>
            <span
                class="activity"
                :class="siteDetails.fileClassification.toLowerCase()"
            >{{ siteDetails.fileClassificationDescription }}</span>
        </div>
        <div class="fifth-text-row">
            <span class="subtitle">Year(s) of Report(s)</span>
            {{ siteDetails.yearOfESAReport }}
        </div>
        <div class="fifth-text-row">
            <span class="subtitle">Operator (at the time of the report)</span>
            {{ siteDetails.operatorName }}
        </div>
        <div class="half-row">
            <div class="col">
                <span class="subtitle">Additional Event Comments</span>
                <p>{{ siteDetails.additionalEventComments }}</p>
            </div>
            <div class="col">
                <span class="subtitle">Additional Risk Management Comments</span>
                <p>{{ siteDetails.additionalRiskManagementComments }}</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import ESARSite from '@/models/ESARSite';

import AgriculturalIcon from '@/assets/img/agricultural-icon.svg';
import CommercialIcon from '@/assets/img/commercial-icon.svg';
import IndustrialIcon from '@/assets/img/industrial-icon.svg';
import NaturalIcon from '@/assets/img/natural-icon.svg';
import ResidentialIcon from '@/assets/img/residential-icon.svg';
import ResidentialParklandIcon from '@/assets/img/residential-parkland-icon.svg';
import SensetiveLandNoneIcon from '@/assets/img/sensetive-land-none.svg';

import Pill from '../Pill/Pill.vue';

export default defineComponent({
    name: 'Details',
    components: {
        Pill
    },
    props: {
        siteDetails: ESARSite
    },
    setup(props) {
        const apiKey = `key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`;

        const siteStatusPillColor = computed(() => {
            const status = props.siteDetails?.currentSiteStatus || '';
            if (['OPN', 'CER'].includes(status)) {
                return 'blue';
            }
            if (['PND', 'SUS'].includes(status)) {
                return 'red';
            }
            return 'grey';
        });

        const landUseCriteria = computed(() => {
            const images = [];
            if (props.siteDetails?.isAgricultural.toLowerCase().includes('y')) {
                images.push([AgriculturalIcon, 'Agricultural']);
            }
            if (props.siteDetails?.isCommercial.toLowerCase().includes('y')) {
                images.push([CommercialIcon, 'Commercial']);
            }
            if (props.siteDetails?.isIndustrial.toLowerCase().includes('y')) {
                images.push([IndustrialIcon, 'Industrial']);
            }
            if (props.siteDetails?.isNatural.toLowerCase().includes('y')) {
                images.push([NaturalIcon, 'Natural']);
            }
            if (props.siteDetails?.isResidential.toLowerCase().includes('y')) {
                images.push([ResidentialIcon, 'Residential']);
            }

            if (images.length === 0) {
                images.push([SensetiveLandNoneIcon, 'None']);
            }

            return images;
        });

        const mostSensitiveLandUseWithin30m = computed(() => {
            let res = [SensetiveLandNoneIcon, 'None'];

            if (props.siteDetails?.isMoreSensetiveLandWithin30m.toLowerCase() !== 'yes') {
                return res;
            }

            switch (props.siteDetails?.mostSensetiveLandUse.toLowerCase()) {
            default: break;
            case 'res/park':
                res = [ResidentialParklandIcon, 'Residential/Park'];
                break;
            }

            return res;
        });

        const [mostSensitiveLandUseWithin30mImage, mostSensitiveLandUseWithin30mLabel] = mostSensitiveLandUseWithin30m.value;

        const allSubstancesRemediatedToCriteria = computed(() => {
            const val = props.siteDetails?.allSubstancesRemediatedToCriteria.toLowerCase();
            if (val === 'y') {
                return 'yes';
            }
            if (val === 'n') {
                return 'no';
            }
            return 'n/a';
        });
        const allSubstancesRemediatedToCriteriaColor = computed(() => {
            if (allSubstancesRemediatedToCriteria.value === 'yes') {
                return 'blue';
            }
            if (allSubstancesRemediatedToCriteria.value === 'no') {
                return 'red';
            }
            return 'grey';
        });

        const humanHealthConcernsOrExceedancesColor = computed(() => {
            const val = props.siteDetails?.humanHealthConcernsOrExceedances.toLowerCase() || 'na';
            if (['n', 'no'].includes(val)) {
                return 'blue';
            }
            if (['na', 'n/a'].includes(val)) {
                return 'grey';
            }
            return 'red';
        });

        const googleMapSrc = computed(() => {
            const markers = props.siteDetails?.coordinates.map((i) => `markers=color:red|size:mid|${i?.latitude},${i?.longitude}`) || [];

            return [
                `https://maps.googleapis.com/maps/api/staticmap?${apiKey}`,
                'size=400x350',
                'maptype=satellite',
                ...markers
            ].join('&');
        });

        return {
            googleMapSrc,
            allSubstancesRemediatedToCriteria,
            allSubstancesRemediatedToCriteriaColor,
            humanHealthConcernsOrExceedancesColor,
            siteStatusPillColor,
            landUseCriteria,
            mostSensitiveLandUseWithin30mImage,
            mostSensitiveLandUseWithin30mLabel
        };
    }
});
</script>

<style lang="postcss" scoped>
.panel {
    .subtitle {
        @apply text-sm font-bold;
    }

    .image {
        @apply w-detail-image h-detail-image;
    }

    .notes {
        @apply block text-xs w-full;
    }

    .toupper {
        @apply uppercase;
    }

   .activity {
        @apply text-black;

        &.csu {
            @apply text-map-contaminated-site-upstream;
        }

        &.rec {
            @apply text-map-reclamation;
        }

        &.pst {
            @apply text-map-petroleum-storage-tank;
        }

        &.scd {
            @apply text-map-site-contamination-and-decomissioning;
        }
    }

    /** Row Types */
    .half-text-row,
    .third-text-row,
    .fifth-text-row,
    .pill-row,
    .half-row {
        @apply flex text-sm mb-3 flex-wrap;
    }

    .half-text-row {
        .subtitle {
            @apply w-2/5 mr-2;
        }
    }

    .third-text-row {
        .subtitle {
            @apply w-1/3 mr-2;
        }
    }

    .fifth-text-row {
        .subtitle {
            @apply w-1/5 mr-2;
        }
    }

    .pill-row {
        @apply justify-between;

        &.shadded {
            @apply bg-grey-100 p-1;
        }

    }

    .half-row {
        @apply flex justify-between;

        .col {
            @apply block;
            width: 49%;
        }
    }

    .land-use {
        @apply flex flex-wrap;

        .criteria {
            @apply inline-block mb-4;
            min-width: 50%;

            .subtitle {
                @apply mb-5;
            }
        }

        .most-sensitive {
            @apply inline-block;
            min-width: 40%;
        }

        .criteria,
        .most-sensitive {
            .land-type {
                @apply inline-block w-20 mr-6;

                &:last-of-type() {
                    @apply mr-0;
                }

                img {
                    @apply w-full;
                }
            }
        }
    }
}
</style>
